// src/components/ParticlesNetwork.jsx (ملف معدل وجاهز)
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';

const CanvasContainer = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: auto; /* تفعيل التفاعل مع الماوس */
  z-index: 1;
  opacity: 1; /* شفافية كاملة لوضوح أفضل */
`;

// الخطوة 1: تحويل الإعدادات الثابتة إلى دالة ديناميكية
const getDynamicConfig = (width) => {
    const isMobile = width < 768;

    return {
        // إذا كان العرض صغيراً (موبايل)، استخدم 35 جسيماً، وإلا استخدم 85
        particleCount: isMobile ? 35 : 85, 
        particleSpeed: 0.8, 
        // قلل المسافة بين الخطوط على الموبايل لتقليل الكثافة
        lineDistance: isMobile ? 90 : 130,
        particleSize: { min: 2, max: 4 }, 
        colors: [
            '#90bfdf', '#ffffff', '#00f5ff', '#8a2be2', '#ff69b4', '#20b2aa'
        ],
        lineOpacity: 0.6,
        particleOpacity: 0.85, 
        // قلل مسافة التفاعل مع الماوس على الموبايل
        mouseInteractionDistance: isMobile ? 100 : 180,
        mouseLineOpacity: 0.8,
        glowEffect: true,
    };
};

class Particle {
    // الخطوة 3: تعديل الكلاس ليقبل الإعدادات كمعامل
    constructor(canvas, config) {
        this.canvas = canvas;
        this.config = config; // حفظ الإعدادات المستلمة
        this.reset();
        this.color = this.config.colors[Math.floor(Math.random() * this.config.colors.length)];
        this.size = Math.random() * (this.config.particleSize.max - this.config.particleSize.min) + this.config.particleSize.min;
        this.originalSize = this.size;
        this.pulse = Math.random() * Math.PI * 2;
    }

    reset() {
        this.x = Math.random() * this.canvas.width;
        this.y = Math.random() * this.canvas.height;
        // استخدام الإعدادات المحفوظة بدلاً من العامة
        this.vx = (Math.random() - 0.5) * this.config.particleSpeed;
        this.vy = (Math.random() - 0.5) * this.config.particleSpeed;
    }

    update(time = 0) {
        this.x += this.vx;
        this.y += this.vy;
        
        this.pulse += 0.02;
        this.size = this.originalSize + Math.sin(this.pulse) * 0.3;

        if (this.x < 0 || this.x > this.canvas.width) this.vx *= -0.9;
        if (this.y < 0 || this.y > this.canvas.height) this.vy *= -0.9;
    }

    draw(ctx) {
        ctx.save();
        
        if (this.config.glowEffect) {
            ctx.shadowColor = this.color;
            ctx.shadowBlur = 8;
        }
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.config.particleOpacity;
        ctx.fill();
        
        if (this.config.glowEffect) {
            ctx.shadowBlur = 0;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.size * 0.5, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.globalAlpha = 0.4;
            ctx.fill();
        }
        
        ctx.restore();
    }

    distanceToMouse(mouseX, mouseY) {
        const dx = this.x - mouseX;
        const dy = this.y - mouseY;
        return Math.sqrt(dx * dx + dy * dy);
    }
}

const ParticlesNetwork = () => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const mouseRef = useRef({ x: null, y: null });
    const particlesRef = useRef([]);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const timeRef = useRef(0);

    // الخطوة 2: إنشاء كائن إعدادات متجاوب يتغير مع تغير عرض الشاشة
    const config = useMemo(() => getDynamicConfig(dimensions.width), [dimensions.width]);

    const updateDimensions = useCallback(() => {
        const container = canvasRef.current?.parentElement;
        if (container) {
            const rect = container.getBoundingClientRect();
            setDimensions({ width: rect.width, height: rect.height });
        }
    }, []);

    // تعديل الدالة لتستخدم الإعدادات الديناميكية
    const initParticles = useCallback((canvas) => {
        particlesRef.current = [];
        for (let i = 0; i < config.particleCount; i++) {
            // تمرير الإعدادات الصحيحة عند إنشاء كل جسيم
            particlesRef.current.push(new Particle(canvas, config));
        }
    }, [config]); // الآن هذه الدالة تعتمد على الإعدادات الحالية

    const drawConnections = useCallback((ctx, particles, mouseX, mouseY) => {
        ctx.save();
        
        for (let i = 0; i < particles.length; i++) {
            for (let j = i + 1; j < particles.length; j++) {
                const dx = particles[i].x - particles[j].x;
                const dy = particles[i].y - particles[j].y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance < config.lineDistance) {
                    const opacity = (1 - distance / config.lineDistance) * config.lineOpacity;
                    ctx.globalAlpha = opacity;
                    const gradient = ctx.createLinearGradient(particles[i].x, particles[i].y, particles[j].x, particles[j].y);
                    gradient.addColorStop(0, particles[i].color);
                    gradient.addColorStop(1, particles[j].color);
                    ctx.beginPath();
                    ctx.moveTo(particles[i].x, particles[i].y);
                    ctx.lineTo(particles[j].x, particles[j].y);
                    ctx.strokeStyle = gradient;
                    ctx.lineWidth = 0.8;
                    ctx.stroke();
                }
            }
        }

        if (mouseX !== null && mouseY !== null) {
            for (let i = 0; i < particles.length; i++) {
                const mouseDistance = particles[i].distanceToMouse(mouseX, mouseY);
                if (mouseDistance < config.mouseInteractionDistance) {
                    const opacity = (1 - mouseDistance / config.mouseInteractionDistance) * config.mouseLineOpacity;
                    ctx.globalAlpha = opacity;
                    ctx.beginPath();
                    ctx.moveTo(particles[i].x, particles[i].y);
                    ctx.lineTo(mouseX, mouseY);
                    ctx.strokeStyle = '#00f5ff';
                    ctx.lineWidth = 1.2;
                    ctx.shadowColor = '#00f5ff';
                    ctx.shadowBlur = 6;
                    ctx.stroke();
                    ctx.shadowBlur = 0;
                }
            }
        }
        
        ctx.restore();
    }, [config]); // هذه الدالة تعتمد أيضاً على الإعدادات

    const animate = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        timeRef.current += 1;
        particlesRef.current.forEach(particle => {
            particle.update(timeRef.current);
            particle.draw(ctx);
        });
        drawConnections(ctx, particlesRef.current, mouseRef.current.x, mouseRef.current.y);

        animationRef.current = requestAnimationFrame(animate);
    }, [drawConnections]);

    const handleMouseMove = useCallback((event) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        mouseRef.current = {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        };
        
        particlesRef.current.forEach(particle => {
            const distance = particle.distanceToMouse(mouseRef.current.x, mouseRef.current.y);
            if (distance < config.mouseInteractionDistance) {
                const force = (config.mouseInteractionDistance - distance) / config.mouseInteractionDistance;
                const dx = (particle.x - mouseRef.current.x) / distance;
                const dy = (particle.y - mouseRef.current.y) / distance;
                
                particle.vx += dx * force * 0.5;
                particle.vy += dy * force * 0.5;
                
                const maxSpeed = config.particleSpeed * 2;
                particle.vx = Math.max(-maxSpeed, Math.min(maxSpeed, particle.vx));
                particle.vy = Math.max(-maxSpeed, Math.min(maxSpeed, particle.vy));
            }
        });
    }, [config]); // هذه الدالة أيضاً تعتمد على الإعدادات

    const handleMouseLeave = useCallback(() => {
        mouseRef.current = { x: null, y: null };
    }, []);

    useEffect(() => {
        updateDimensions();
        
        let resizeTimer;
        const throttledResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(updateDimensions, 100);
        };

        window.addEventListener('resize', throttledResize);
        return () => {
            window.removeEventListener('resize', throttledResize);
            clearTimeout(resizeTimer);
        };
    }, [updateDimensions]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || dimensions.width === 0 || dimensions.height === 0) return;

        canvas.width = dimensions.width;
        canvas.height = dimensions.height;

        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        initParticles(canvas);
        
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        animate();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [dimensions, initParticles, animate]);

    return (
        <CanvasContainer
            ref={canvasRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        />
    );
};

export default React.memo(ParticlesNetwork);